import type { I18nKey } from "@/core/i18n";
import { EvalActionTypeEnum, type EvalAction, type EvalGpioAction } from "@repo/schema";
import type { TFunction } from "i18next";
import type { LucideIcon } from "lucide-react";
import type { ComponentType } from "react";

/** Robopipe pin indices, not Luxonis GPIO numbers. */
export const GPIO_PINS = [1, 2] as const;

export function isGpioAction(action: EvalAction): action is EvalGpioAction {
  return action.type === EvalActionTypeEnum.GPIO;
}

export type ActionFieldsProps = {
  /** Index into the form's `actions` array; used to build field paths. */
  index: number;
  /** The whole list, so a type can constrain itself against its siblings. */
  actions: EvalAction[];
};

/**
 * Everything the UI knows about one action type, in one place. The add menu, the
 * action rows, the inline settings panel and the limits-table chips all read
 * from here, so introducing an action type means adding a descriptor (plus a
 * `Fields` component if it is configurable) and touching nothing else.
 *
 * Lives apart from actionRegistry.ts so that a `Fields` component can import
 * these shared types without a cycle back through the descriptor list.
 */
export type ActionDescriptor = {
  type: EvalActionTypeEnum;
  label: I18nKey<"evaluation">;
  /** One-liner shown beneath the label in the add menu. */
  description: I18nKey<"evaluation">;
  Icon: LucideIcon;
  /** Chip tint in the limits overview table. */
  badgeVariant: "default" | "secondary" | "destructive" | "outline";
  createDefault: (actions: EvalAction[]) => EvalAction;
  /**
   * Collapsed-row summary. Omitted for types that carry no configuration — such
   * a row renders no summary and no expander.
   */
  summarize?: (action: EvalAction, t: TFunction<["evaluation", "common"]>) => string;
  /**
   * Inline settings panel. Its presence is the single source of truth for "this
   * type is configurable", so nothing else needs to branch on the type.
   */
  Fields?: ComponentType<ActionFieldsProps>;
  /**
   * Types this one displaces when added. Expresses the alert/warning exclusion
   * as data, so the user swaps in one click instead of deleting first.
   */
  replaces?: EvalActionTypeEnum[];
  /** Human reason this type cannot be added right now, or null when it can. */
  unavailableReason?: (actions: EvalAction[]) => I18nKey<"evaluation"> | null;
};
